import { useState } from 'react';
import { Link } from 'react-router-dom';
import SectionCard from '../../components/SectionCard.jsx';
import ConsoleTable from '../../components/ConsoleTable.jsx';
import ExternalLink from '../../components/ExternalLink.jsx';
import StatusWord, { SeverityWord } from '../../components/StatusWord.jsx';
import { formatMoney, impactWords } from '../../lib/license-format.js';

/**
 * "Reclaim first" and "Needs attention" (spec 5.1): numbered rows, the sentence is the button that expands the row,
 * money per month and seats on the right, a muted line with the owner and the one next step. Expanded: impact, how to
 * fix, the products, the people (only when the view carries them: the customer's own admin), and two links. A finding
 * onGuard could not evaluate stays in the list with "Not evaluated", the reason, and the one action that unlocks it.
 *
 * `checklist` (the reclaim checklist, Hugo Andrade 2026-10-30): every check is a row with its status. A check with
 * nothing to do reads as done (a check mark, its OK words, "OK"); one with something to do is numbered in the order to
 * act; one onGuard could not evaluate says what is missing. `usage` (view.usage) is shown in the LIC-008 row.
 */
export default function FindingList({ title, description, findings, currency, numbered = true, showSeverity = false, unlockTo, testId, emptyText, checklist = false, usage = null, header = null }) {
  const [open, setOpen] = useState(() => new Set());
  const toggle = (key) => setOpen((prev) => {
    const next = new Set(prev);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  });
  return (
    <SectionCard title={title} description={description} testId={testId}>
      {header}
      {findings.length === 0 ? (
        <p className="text-sm text-ipc-muted">{emptyText}</p>
      ) : (
        <ol className="divide-y divide-ipc-border">
          {findings.map((f, i) => {
            const expanded = open.has(f.key);
            const evaluated = f.evaluated === true;
            const { money, units } = impactWords(f, currency);
            const panelId = `${testId}-${i}`;
            if (checklist && f.status === 'ok') return <OkRow key={f.key} finding={f} />;
            const marker = checklist
              ? (f.status === 'act' ? <span aria-hidden="true" className="w-6 shrink-0 font-display text-lg font-extrabold leading-tight text-ipc-primary">{findings.filter((x) => x.status === 'act').indexOf(f) + 1}</span> : <NotEvaluatedMark />)
              : (numbered ? <span aria-hidden="true" className="w-6 shrink-0 font-display text-lg font-extrabold leading-tight text-ipc-primary">{i + 1}</span> : null);
            return (
              <li key={f.key} className="py-3 first:pt-1" data-finding={f.key} data-status={f.status}>
                <div className={`flex flex-col gap-1.5 ${numbered ? 'sm:flex-row sm:items-start sm:gap-3' : ''}`}>
                  {marker}
                  <div className="min-w-0 flex-1">
                    <button
                      type="button" className="tap text-left font-semibold text-ipc-ink hover:text-ipc-light hover:underline"
                      aria-expanded={expanded} aria-controls={panelId} onClick={() => toggle(f.key)}
                    >
                      {f.title}
                    </button>
                    {evaluated ? (
                      <p className="mt-0.5 text-[13px] text-ipc-muted">{f.owner}: {f.nextStep}.</p>
                    ) : (
                      <p className="mt-0.5 text-[13px] text-ipc-muted" data-not-evaluated="">
                        <span className="font-semibold text-ipc-ink">Not evaluated.</span> {f.evaluated.reason}{' '}
                        {f.evaluated.unlock && unlockTo?.(f.evaluated.unlock) ? <Link className="font-semibold text-ipc-light hover:underline" to={unlockTo(f.evaluated.unlock)}>{f.evaluated.unlock === 'concealed' ? 'How to show names' : 'Open Data collection'}</Link> : null}
                      </p>
                    )}
                  </div>
                  {evaluated ? (
                    <span className={`flex shrink-0 items-center gap-3 whitespace-nowrap text-[13px] ${numbered ? 'sm:ml-auto sm:text-right' : 'order-first'}`}>
                      {showSeverity ? <SeverityWord severity={f.impact.severity} /> : null}
                      {money ? <span className="font-semibold tabular-nums text-ipc-ink">{money}</span> : null}
                      <span className="tabular-nums text-ipc-muted">{units}</span>
                    </span>
                  ) : null}
                </div>
                {expanded ? <FindingDetail id={panelId} finding={f} currency={currency} numbered={numbered} usage={f.id === 'LIC-008' ? usage : null} /> : null}
              </li>
            );
          })}
        </ol>
      )}
    </SectionCard>
  );
}

/** A check with nothing to do: a check mark, its OK words and "OK". Nothing to expand. */
function OkRow({ finding: f }) {
  return (
    <li className="py-3 first:pt-1" data-finding={f.key} data-status="ok">
      <div className="flex flex-col gap-1.5 sm:flex-row sm:items-start sm:gap-3">
        <span aria-hidden="true" className="flex w-6 shrink-0 justify-start pt-0.5 text-ipc-primary"><CheckMark /></span>
        <p className="min-w-0 flex-1 text-ipc-ink">{f.okTitle ?? f.title}</p>
        <span className="shrink-0 text-[13px] sm:ml-auto"><StatusWord tone="good">OK</StatusWord></span>
      </div>
    </li>
  );
}

function CheckMark() {
  return (
    <svg viewBox="0 0 20 20" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" focusable="false">
      <path d="M4 10.5l4 4 8-9" />
    </svg>
  );
}

function NotEvaluatedMark() {
  return (
    <span aria-hidden="true" className="flex w-6 shrink-0 justify-start pt-0.5 text-ipc-muted">
      <svg viewBox="0 0 20 20" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" focusable="false"><circle cx="10" cy="10" r="7.5" /><path d="M6.5 10h7" /></svg>
    </span>
  );
}

function FindingDetail({ id, finding: f, currency, numbered, usage }) {
  const g = f.guidance;
  return (
    <div id={id} className={`mt-3 space-y-4 text-sm ${numbered ? 'sm:pl-9' : ''}`} data-finding-detail="">
      <div>
        <h3 className="text-[13px] font-semibold uppercase tracking-wide text-ipc-muted">Impact</h3>
        <p className="mt-1 text-ipc-ink">{g.impact}</p>
        {f.impact.partial ? <p className="mt-1 text-[13px] text-ipc-muted">Some of these products have no price, so the amount counts only the ones that do.</p> : null}
        {f.inGrace ? <p className="mt-1 text-[13px] text-ipc-muted">{f.inGrace.seats.toLocaleString('en-US')} more on accounts disabled less than {f.inGrace.days} days ago are not counted yet (your grace period).</p> : null}
      </div>
      <div>
        <h3 className="text-[13px] font-semibold uppercase tracking-wide text-ipc-muted">How to fix</h3>
        <ol className="mt-1 list-decimal space-y-1 pl-5 text-ipc-ink">{g.steps.map((s) => <li key={s}>{s}</li>)}</ol>
      </div>
      {f.products?.length ? (
        <div>
          <h3 className="text-[13px] font-semibold uppercase tracking-wide text-ipc-muted">By product</h3>
          <ul className="mt-1 space-y-0.5">
            {f.products.map((p) => (
              <li key={p.skuId} className="flex flex-wrap gap-x-2 text-ipc-ink">
                <span>{p.name}</span>
                <span className="tabular-nums text-ipc-muted">{p.seats.toLocaleString('en-US')} {p.seats === 1 ? 'seat' : 'seats'}{formatMoney(p.monthly, currency) ? `, ${formatMoney(p.monthly, currency)}/mo` : ''}</span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
      {f.services ? (
        <p className="text-[13px] text-ipc-muted">Licensed, never used: Teams {f.services.teams.toLocaleString('en-US')}, Exchange {f.services.exchange.toLocaleString('en-US')}, OneDrive {f.services.onedrive.toLocaleString('en-US')}, SharePoint {f.services.sharepoint.toLocaleString('en-US')} people. Candidates for a smaller plan, not counted as savings.</p>
      ) : null}
      {usage ? <UsageLists usage={usage} /> : null}
      {Array.isArray(f.people) ? <PeopleTable finding={f} /> : null}
      <div className="flex flex-wrap gap-x-5 gap-y-1">
        {g.links.map((l) => <ExternalLink key={l.url} href={l.url} className="text-[13px] font-semibold text-ipc-light hover:underline">{l.label}</ExternalLink>)}
      </div>
    </div>
  );
}

/** Which products onGuard checks for use and which it cannot (Hugo Andrade, 2026-10-30: be explicit). */
export function UsageLists({ usage }) {
  return (
    <div className="space-y-1.5 text-ipc-ink">
      <p>
        <span className="font-semibold">onGuard checks: </span>
        {usage.checked.length ? usage.checked.map((p) => `${p.name} (${p.services.join(', ')})`).join('; ') : 'none of your products.'}
      </p>
      <p>
        <span className="font-semibold">onGuard cannot check: </span>
        {usage.notChecked.length ? `${usage.notChecked.map((p) => p.name).join(', ')}. Microsoft reports no per-person use of these products in the data onGuard reads.` : 'none: every product you hold can be checked.'}
      </p>
    </div>
  );
}

function PeopleTable({ finding: f }) {
  if (f.people.length === 0) return null;
  const columns = [
    { id: 'name', header: 'Name', sortValue: (p) => String(p.name ?? '').toLowerCase(), cell: (p) => <span><span className="font-medium text-ipc-ink">{p.name ?? 'No name'}</span><span className="block text-[12.5px] text-ipc-muted [overflow-wrap:anywhere]">{p.upn}</span></span> },
    { id: 'product', header: 'Product', hideBelow: 'md', sortValue: (p) => p.product, cell: (p) => p.product },
    { id: 'detail', header: 'Why', sortValue: (p) => p.detail ?? '', cell: (p) => p.detail },
    { id: 'group', header: 'Assigned through', hideBelow: 'lg', sortValue: (p) => p.group ?? '', cell: (p) => p.group ?? 'Directly' },
  ];
  return (
    <div>
      <h3 className="text-[13px] font-semibold uppercase tracking-wide text-ipc-muted">People ({f.people.length.toLocaleString('en-US')})</h3>
      {f.peopleTruncated ? <p className="mt-1 text-[13px] text-ipc-muted">onGuard lists the first people of each collection here; the export and your data bucket (_license-insights) have the same list. Counts above are complete.</p> : null}
      <div className="mt-2">
        <ConsoleTable
          label={`People: ${f.title}`}
          framed={false}
          columns={columns}
          rows={f.people}
          getRowId={(p) => `${p.id}:${p.skuId}`}
          rowName={(p) => `${p.name ?? p.upn} ${p.product}`}
          searchText={(p) => `${p.name ?? ''} ${p.upn ?? ''} ${p.product} ${p.group ?? ''}`}
          searchPlaceholder="Search people"
          pageSize={10}
        />
      </div>
    </div>
  );
}
